import { EmptyState } from '@/components/primitives/EmptyState'
import type { StoredPage } from '@/lib/scraper'
import { FINAL_STATUSES, scraperApi, useJobEvents } from '@/lib/scraper'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ListChecks } from 'lucide-react'
import { useState } from 'react'
import { JobProgress } from './JobProgress'
import { ResultView } from './ResultView'
import {
  BTN_SECONDARY,
  CARD,
  CHIP_OFF,
  CHIP_ON,
  MUTED,
  PILL,
  SKELETON,
  STATUS_PILL,
  cls
} from './ui'

export function JobsPanel({
  jobId,
  onSelect
}: { jobId: string | null; onSelect: (id: string | null) => void }) {
  return jobId ? (
    <JobDetailView jobId={jobId} onBack={() => onSelect(null)} />
  ) : (
    <JobList onSelect={onSelect} />
  )
}

function JobList({ onSelect }: { onSelect: (id: string) => void }) {
  const [status, setStatus] = useState('')
  const jobs = useQuery({
    queryKey: ['scraper-jobs', status],
    queryFn: () => scraperApi.jobs(status ? `?status=${status}` : ''),
    refetchInterval: 5000
  })
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {['', 'running', 'completed', 'failed', 'cancelled'].map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => setStatus(s)}
            aria-pressed={status === s}
            className={status === s ? CHIP_ON : CHIP_OFF}
          >
            {s || 'all'}
          </button>
        ))}
      </div>
      {jobs.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={cls(SKELETON, 'h-16')} />
          ))}
        </div>
      ) : jobs.isError ? (
        <p className="text-sm text-red-500">Could not load jobs.</p>
      ) : !jobs.data?.results.length ? (
        <EmptyState
          icon={ListChecks}
          title="No jobs yet"
          description="Start a scrape, crawl, map or extract from the Playground."
          className="rounded-[36px] border-neutral-200 dark:border-neutral-800"
        />
      ) : (
        <ul className="space-y-2">
          {jobs.data.results.map((job) => (
            <li key={job.id}>
              <button
                type="button"
                onClick={() => onSelect(job.id)}
                className="flex w-full flex-wrap items-center gap-3 rounded-[36px] border border-neutral-200 bg-white px-5 py-3 text-left transition-colors hover:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-600"
              >
                <span className={STATUS_PILL[job.status]}>{job.status}</span>
                <span className="text-sm font-bold capitalize text-neutral-900 dark:text-white">
                  {job.kind}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-neutral-600 dark:text-neutral-300">
                  {job.url}
                </span>
                <span className={MUTED}>
                  {job.pages_done}/{job.pages_total} ·{' '}
                  {new Date(job.created_at).toLocaleString()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function JobDetailView({
  jobId,
  onBack
}: { jobId: string; onBack: () => void }) {
  const events = useJobEvents(jobId)
  const [pageNo, setPageNo] = useState(1)
  const [openPage, setOpenPage] = useState<StoredPage | null>(null)
  const job = useQuery({
    queryKey: ['scraper-job', jobId],
    queryFn: () => scraperApi.job(jobId),
    refetchInterval: (q) =>
      q.state.data && FINAL_STATUSES.includes(q.state.data.status)
        ? false
        : 2000
  })
  const pages = useQuery({
    queryKey: ['scraper-job-pages', jobId, pageNo],
    queryFn: () => scraperApi.pages(jobId, pageNo),
    refetchInterval:
      job.data && FINAL_STATUSES.includes(job.data.status) ? false : 4000
  })

  if (openPage) {
    const extractions = (job.data?.extractions ?? []).filter(
      (e) => e.page === openPage.id
    )
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setOpenPage(null)}
          className={BTN_SECONDARY}
        >
          <ArrowLeft className="size-4" /> Back to pages
        </button>
        <ResultView
          page={{ ...openPage, screenshot: openPage.screenshot_url }}
          extractions={extractions}
        />
      </div>
    )
  }

  const siteExtractions = (job.data?.extractions ?? []).filter(
    (e) => e.page === null
  )
  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className={BTN_SECONDARY}>
        <ArrowLeft className="size-4" /> All jobs
      </button>
      {job.isLoading || !job.data ? (
        <div className={cls(SKELETON, 'h-32')} />
      ) : (
        <JobProgress job={job.data} events={events} />
      )}
      {siteExtractions.length > 0 && (
        <ResultView
          page={{ url: job.data?.url ?? '' }}
          extractions={siteExtractions}
        />
      )}
      <div className={CARD}>
        <h3 className="mb-3 text-sm font-semibold text-neutral-900 dark:text-white">
          Pages ({pages.data?.count ?? 0})
        </h3>
        {pages.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={cls(SKELETON, 'h-10')} />
            ))}
          </div>
        ) : !pages.data?.results.length ? (
          <p className={MUTED}>No pages yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {pages.data.results.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setOpenPage(p)}
                  className="flex w-full flex-wrap items-center gap-2 rounded-[24px] px-3 py-2 text-left transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <span className={PILL}>{p.tier || 'failed'}</span>
                  <span className={PILL}>{p.page_role}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-neutral-800 dark:text-neutral-200">
                    {p.url}
                  </span>
                  <span className={MUTED}>{p.sections.length} sections</span>
                  {p.error && (
                    <span className="w-full truncate text-[11px] text-red-500">
                      {p.error}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {pages.data && (pages.data.next || pages.data.previous) && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={!pages.data.previous}
              onClick={() => setPageNo((n) => n - 1)}
              className={BTN_SECONDARY}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!pages.data.next}
              onClick={() => setPageNo((n) => n + 1)}
              className={BTN_SECONDARY}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
